import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Lightbox from '../components/Lightbox';
import { LinksField, TagsField } from '../components/ListFields';
import { useAuth } from '../lib/auth';
import { type Extraction, MAX_CAPTURE_LENGTH, extractIdea, plainNoteTitle } from '../lib/capture';
import { rejectReason, uploadIdeaImage } from '../lib/images';
import { attachIdeaImages, createIdea } from '../lib/useIdeas';

/** An image picked for the note but not uploaded yet, with a local preview. */
interface Pending {
  file: File;
  preview: string;
}

function messageFor(e: unknown): string {
  const code = (e as { code?: string }).code ?? '';
  return `${(e as Error).message}${code ? ` (${code})` : ''}`;
}

/**
 * `/new` — typed capture, in the same two steps the phones use.
 *
 * First the idea as you would say it; then a review of what will be saved, reached either
 * through `processTranscript` or by taking the text as it stands. The review step is not
 * redundant for a plain note: as on iOS and Android, it is the one chance to add tags, links
 * or images before the note is written.
 *
 * Nothing touches Firestore until Save. Images are held locally until then because the
 * Storage rules refuse an upload under an idea document that does not exist yet — so the
 * order is document, then bytes, then the URLs onto the document, as `NoteComposer.save`
 * does on Android.
 */
export default function CreateIdea() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [step, setStep] = useState<'write' | 'review'>('write');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [recallHints, setRecallHints] = useState<Extraction['recallHints']>();
  const [pending, setPending] = useState<Pending[]>([]);
  const [viewing, setViewing] = useState<number | null>(null);

  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Object URLs hold the file in memory until revoked. Revoked on removal below, and here
  // for whatever is still picked when the screen goes away.
  const previews = useRef<string[]>([]);
  previews.current = pending.map((p) => p.preview);
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_CAPTURE_LENGTH;
  const busy = extracting || saving;

  function review(fields: Extraction) {
    setTitle(fields.title);
    setDescription(fields.description);
    setTags(fields.tags);
    setLinks(fields.links);
    setRecallHints(fields.recallHints);
    setError(null);
    setStep('review');
  }

  async function withAi() {
    setExtracting(true);
    setError(null);
    try {
      review(await extractIdea(trimmed));
    } catch (e) {
      // The text is still in the box, so a failed extraction costs a retry or a plain save,
      // never the idea itself.
      setError(`Could not fill this in with AI: ${messageFor(e)}`);
    } finally {
      setExtracting(false);
    }
  }

  function withoutAi() {
    // Empty tags by design, as on Android: inventing them would put unearned words into
    // search. No recall hints either — nothing ran an extraction.
    review({ title: plainNoteTitle(trimmed), description: trimmed, tags: [], links: [] });
  }

  function addFiles(files: FileList | null) {
    if (!files?.length) return;
    const rejected = Array.from(files).map(rejectReason).filter(Boolean);
    if (rejected.length) {
      setError(rejected.join(' '));
      return;
    }
    setError(null);
    setPending((current) => [
      ...current,
      ...Array.from(files).map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  }

  function removePending(preview: string) {
    URL.revokeObjectURL(preview);
    setPending((current) => current.filter((p) => p.preview !== preview));
  }

  async function save() {
    if (!user) return;
    setSaving(true);
    setError(null);

    let id: string;
    try {
      id = await createIdea(user.uid, {
        // iOS requires a title string, and a blank one renders as "Untitled" everywhere.
        // Better to name it the way a plain note is named.
        title: title.trim() || plainNoteTitle(description),
        description: description.trim(),
        tags,
        links,
        transcript: trimmed,
        recallHints,
      });
    } catch (e) {
      // Visible, like every other failure path here. Nothing was written, so staying put
      // and letting Save be pressed again is safe.
      setError(messageFor(e));
      setSaving(false);
      return;
    }

    if (pending.length === 0) {
      navigate('/');
      return;
    }

    // The note exists from here on, so a failed upload must not fail the save — losing the
    // note over a photo is the worse outcome, and Android makes the same call. It also must
    // not leave this form up, where pressing Save again would create a second note. So the
    // failure goes to the note's own editor, where the images can be tried again.
    try {
      const urls: string[] = [];
      for (const { file } of pending) {
        urls.push(await uploadIdeaImage(user.uid, id, file));
      }
      await attachIdeaImages(user.uid, id, urls);
      navigate('/');
    } catch (e) {
      navigate(`/idea/${id}`, {
        state: { notice: `Note saved, but the images could not be added: ${messageFor(e)}` },
      });
    }
  }

  return (
    <>
      <AppHeader title="New note" />

      <main>
        {step === 'write' ? (
          <form
            className="edit"
            onSubmit={(e) => {
              e.preventDefault();
              void withAi();
            }}
          >
            <label className="field">
              <span className="field-label">Your idea</span>
              <textarea
                rows={8}
                value={text}
                autoFocus
                placeholder="Type it the way you would say it"
                disabled={busy}
                onChange={(e) => setText(e.target.value)}
              />
              <span className={`small ${tooLong ? 'error' : 'muted'}`}>
                {tooLong
                  ? `${trimmed.length.toLocaleString()} characters — the limit is ${MAX_CAPTURE_LENGTH.toLocaleString()}.`
                  : `${trimmed.length.toLocaleString()} characters`}
              </span>
            </label>

            <p className="muted small">
              Fill in with AI writes a title, description, tags and links from what you typed,
              the way the phone apps do. You review everything before it is saved.
            </p>

            {error && <p className="error">{error}</p>}

            <div className="actions">
              <button className="primary" type="submit" disabled={busy || !trimmed || tooLong}>
                {extracting ? 'Filling in…' : 'Fill in with AI'}
              </button>
              <button type="button" disabled={busy || !trimmed} onClick={withoutAi}>
                Continue without AI
              </button>
              <Link className="back" to="/">
                Cancel
              </Link>
            </div>
          </form>
        ) : (
          <form
            className="edit"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label className="field">
              <span className="field-label">Title</span>
              <input
                type="text"
                value={title}
                placeholder="Untitled"
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label className="field">
              <span className="field-label">Description</span>
              <textarea
                rows={6}
                value={description}
                placeholder="What was the idea?"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <TagsField tags={tags} onChange={setTags} />
            <LinksField links={links} onChange={setLinks} />

            <div className="field">
              <span className="field-label">Images</span>
              {pending.length > 0 && (
                <div className="thumbs">
                  {pending.map(({ preview }, i) => (
                    <div className="thumb" key={preview}>
                      <button
                        type="button"
                        className="thumb-open"
                        aria-label={`View image ${i + 1} of ${pending.length}`}
                        onClick={() => setViewing(i)}
                      >
                        <img src={preview} alt="" />
                      </button>
                      <button
                        type="button"
                        className="remove"
                        aria-label="Remove image"
                        disabled={saving}
                        onClick={() => removePending(preview)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="add-image">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={saving}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    // Reset so picking the same file twice in a row still fires onChange.
                    e.target.value = '';
                  }}
                />
                <span>Add images</span>
              </label>
              <p className="muted small hint">Images upload when you save.</p>
            </div>

            {error && <p className="error">{error}</p>}

            <div className="actions">
              <button
                className="primary"
                type="submit"
                disabled={saving || !(title.trim() || description.trim())}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" disabled={saving} onClick={() => setStep('write')}>
                Back
              </button>
            </div>
          </form>
        )}
      </main>

      {viewing !== null && (
        <Lightbox
          images={pending.map((p) => p.preview)}
          start={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
